// Central adapter registration for the web app
// Replace with shadcn/ui components as they are added.

import {
  registerButton,
  registerButtonLink,
  registerDialog,
  registerSelect,
  registerSlider,
  type ButtonAdapter,
  type ButtonLinkAdapter,
} from "@lumi/ui-headless";
import { Link } from "react-router-dom";
import { Button as ShadButton, type ButtonProps } from "./app/shared/components/ui/button";
import { cn } from "./app/shared/components/ui/cn";
import * as ShadDialog from "./app/shared/components/ui/dialog";
import {
  Select as ShadSelect,
  SelectContent as ShadSelectContent,
  SelectItem as ShadSelectItem,
  SelectTrigger as ShadSelectTrigger,
} from "./app/shared/components/ui/select";
import { Slider as ShadSlider } from "./app/shared/components/ui/slider";

/**
 * Map headless button variants to shadcn button variant names
 */
function getButtonVariant(variant?: string): ButtonProps["variant"] {
  switch (variant) {
    case "ghost":
      return "ghost";
    case "compact":
      return "compact";
    case "ghost-sm":
      return "ghost-sm";
    case "overlay":
      return "overlay";
    case "outline":
      return "outline";
    case "primary":
    default:
      return "default";
  }
}

/**
 * Get CSS classes for ButtonLink styling based on variant
 */
function getButtonLinkClasses(variant?: string, className?: string): string {
  const baseClasses =
    "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-semibold transition-all duration-200 disabled:opacity-50 disabled:pointer-events-none shadow-lg hover:shadow-xl";

  const variantClasses: Record<string, string> = {
    default:
      "bg-lumi-accent text-black hover:brightness-110 focus-visible:brightness-125 focus-visible:shadow-none hover:shadow-none px-8 py-3 text-base",
    outline:
      "border border-neutral-700 bg-transparent text-white hover:bg-amber-900/20 hover:text-lumi-accent hover:border-amber-400/60 focus-visible:text-lumi-accent focus-visible:shadow-none hover:shadow-none px-6 py-2",
    ghost:
      "bg-neutral-600/70 hover:bg-neutral-600/90 text-white focus-visible:shadow-none hover:shadow-none px-8 py-3 text-base backdrop-blur-sm",
    compact:
      "bg-lumi-accent text-black hover:brightness-110 focus-visible:brightness-125 focus-visible:shadow-none hover:shadow-none px-4 py-2 text-sm",
    "ghost-sm":
      "bg-transparent hover:text-lumi-accent focus-visible:text-lumi-accent px-3 py-2.5 text-sm shadow-none focus-visible:shadow-none hover:shadow-none",
    overlay:
      "bg-black/50 hover:bg-black/70 text-white focus-visible:text-lumi-accent p-2 rounded-full shadow-none focus-visible:shadow-none hover:shadow-none",
  };

  const variantKey = variant === "primary" ? "default" : variant || "default";
  return cn(baseClasses, variantClasses[variantKey], className);
}

// Temporary basic <button> adapter to keep the demo working
export function registerWebAdapters() {
  const buttonAdapter: ButtonAdapter = ({ children, onPress, disabled, variant, className, type }) => {
    return (
      <ShadButton
        onClick={onPress}
        disabled={disabled}
        variant={getButtonVariant(variant)}
        className={typeof className === "string" ? className : undefined}
        type={type}
      >
        {children}
      </ShadButton>
    );
  };

  registerButton(buttonAdapter);

  // ButtonLink adapter - Uses React Router Link with button styling
  const buttonLinkAdapter: ButtonLinkAdapter = ({ children, href, disabled, variant, className }) => {
    return (
      <Link
        to={href}
        className={getButtonLinkClasses(variant, typeof className === "string" ? className : undefined)}
        aria-disabled={disabled}
        onClick={(e) => {
          if (disabled) {
            e.preventDefault();
          }
        }}
      >
        {children}
      </Link>
    );
  };

  registerButtonLink(buttonLinkAdapter);

  registerDialog(({ open, onOpenChange, title, description, children }) => (
    <ShadDialog.Dialog open={open} onOpenChange={onOpenChange}>
      <ShadDialog.DialogContent>
        {(title || description) && (
          <ShadDialog.DialogHeader>
            {title ? <ShadDialog.DialogTitle>{title}</ShadDialog.DialogTitle> : null}
            {description ? <ShadDialog.DialogDescription>{description}</ShadDialog.DialogDescription> : null}
          </ShadDialog.DialogHeader>
        )}
        {children}
      </ShadDialog.DialogContent>
    </ShadDialog.Dialog>
  ));

  registerSlider(({ value, ariaLabel, min = 0, max = 100, step = 1, onChange, className }) => (
    <ShadSlider
      className={className}
      value={[value]}
      min={min}
      max={max}
      step={step}
      aria-label={ariaLabel}
      onValueChange={(vals: number[]) => {
        const v = Array.isArray(vals) && typeof vals[0] === "number" ? vals[0] : value;
        onChange(v);
      }}
    />
  ));

  registerSelect(({ value, onChange, options, ariaLabel, className }) => (
    <ShadSelect value={value} onValueChange={onChange}>
      <ShadSelectTrigger aria-label={ariaLabel} className={className}>
        <span className="truncate">{options.find((o) => o.value === value)?.label ?? value}</span>
      </ShadSelectTrigger>
      <ShadSelectContent>
        {options.map((o) => (
          <ShadSelectItem key={o.value} value={o.value}>
            {o.label}
          </ShadSelectItem>
        ))}
      </ShadSelectContent>
    </ShadSelect>
  ));
}
